import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLumisial } from '../context/LumisialContext';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import type { OwnerFondoCampana, SaldoRestringidoCampana } from '../types/fondosCampana';
import type { RespaldoMovimiento } from '../types/comprobante';
import { movimientoFondoCampanaService, type AjusteFondoCampana, type CampanaGastoFinal } from '../services/movimientoFondoCampanaService';
import { balanceService } from '../services/balanceService';
import { puedeOperarDinero } from '../utils/permissions';
import { entidadDelOwnerDeCampana } from '../utils/comprobanteEntidad';
import { fechaEnChile } from '../utils/dateHelpers';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { esResultadoCampanaIncierto } from '../utils/fondosCampanaRpc';
import { formatCurrency } from '../utils/formatters';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { DialogShell } from './ui/DialogShell';
import { Select } from './ui/Select';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { DatePicker } from './ui/DatePicker';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly ownerContext?: OwnerFondoCampana;
  readonly campanaId?: string;
}
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') return error.message;
  return 'No pudimos completar la operación del fondo.';
}
function fechaHoy(): string {
  const fecha = fechaEnChile(new Date());
  if (!fecha) throw new Error('No se pudo determinar la fecha en Chile.');
  return fecha;
}
function Formulario({ owner, campanaId: inicial, onClose, onSuccess }: Omit<Props, 'isOpen' | 'ownerContext'> & {
  readonly owner: OwnerFondoCampana;
}) {
  const { usuario } = useAuth();
  const [campanas, setCampanas] = useState<CampanaGastoFinal[]>([]);
  const [saldos, setSaldos] = useState<SaldoRestringidoCampana[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campanaId, setCampanaId] = useState(inicial ?? '');
  const [tipo, setTipo] = useState<'ingreso' | 'egreso'>('egreso');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState(fechaHoy);
  const [motivo, setMotivo] = useState('');
  const solicitud = useRef<AjusteFondoCampana | null>(null);
  const [incierto, setIncierto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const comprobante = useComprobanteFormulario({
    entidad: entidadDelOwnerDeCampana(owner),
    fechaMovimiento: fecha,
    permiteSubirDespues: true,
    pideMedioPago: true,
    sentidoDinero: tipo,
  });
  const autorizado = puedeOperarDinero(usuario, { ...owner, diocesisId: null, provinciaEclesiasticaId: null });
  const bloqueado = loading || submitting || incierto;
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();
  useEffect(() => {
    let vigente = true;
    Promise.all([movimientoFondoCampanaService.getCampanasDestinoFinal(owner), balanceService.getSaldosRestringidosCampana(owner)])
      .then(([lista, fondos]) => { if (vigente) { setCampanas(lista); setSaldos(fondos); } })
      .catch((error_: unknown) => { if (vigente) setError(getErrorMessage(error_)); })
      .finally(() => { if (vigente) setLoading(false); });
    return () => { vigente = false; };
  }, [owner]);
  const disponible = saldos.find(f => f.campanaId === campanaId)?.disponible;

  /** Sube el comprobante una sola vez; si falla, el mensaje queda en el formulario y no se registra nada. */
  const resolverRespaldo = async (): Promise<RespaldoMovimiento | null> => {
    setSubmitting(true); setError('');
    try {
      return await comprobante.resolver();
    } catch (error_: unknown) {
      const deNegocio = mensajeErrorDeNegocio(error_);
      if (deNegocio) setError(deNegocio);
      else reportarErrorInesperado(error_, 'No pudimos subir el comprobante. Por favor intenta nuevamente.');
      return null;
    } finally { setSubmitting(false); }
  };

  /** Valida lo escrito y fija la solicitud con su respaldo: el reintento de un resultado incierto la reutiliza tal cual. */
  const prepararSolicitud = async (actorId: string): Promise<AjusteFondoCampana | null> => {
    const valor = Number(monto);
    if (!campanas.some(c => c.id === campanaId) || !/^\d+(?:\.\d{1,2})?$/.test(monto)
      || !Number.isFinite(valor) || valor <= 0 || !motivo.trim() || !fecha) {
      setError('Selecciona el fondo e indica un importe positivo, fecha y motivo.'); return null;
    }
    if (tipo === 'egreso' && (disponible === undefined || valor > disponible)) {
      setError('El gasto supera el saldo disponible confirmado del fondo.'); return null;
    }
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { setError(errorComprobante); return null; }
    const respaldo = await resolverRespaldo();
    if (!respaldo) return null;
    return { id: crypto.randomUUID(), owner, campanaId, tipo, monto: valor, fecha, motivo: motivo.trim(), actorId, respaldo };
  };

  const registrar = async (event: FormEvent) => {
    event.preventDefault();
    if (!autorizado || !usuario?.user_id) return;
    const actual = solicitud.current ?? await prepararSolicitud(usuario.user_id);
    if (!actual) return;
    solicitud.current = actual;
    setSubmitting(true); setError('');
    try {
      await movimientoFondoCampanaService.registrar(actual);
      solicitud.current = null; setIncierto(false); comprobante.reiniciar(); onSuccess(); onClose();
    } catch (error_: unknown) {
      const desconocido = esResultadoCampanaIncierto(error_);
      setIncierto(desconocido); if (!desconocido) solicitud.current = null;
      setError(getErrorMessage(error_));
    } finally { setSubmitting(false); }
  };
  return <DialogShell isOpen onClose={onClose} title={`Movimiento del fondo · ${owner.nombre}`} loading={submitting || incierto}
    hasUnsavedChanges={comprobante.hayCambios} closeRef={closeRef}>
    <form onSubmit={registrar} className="space-y-4">
      <FormErrorAlert error={error} />
      <ComprobanteInput comprobante={comprobante} deshabilitado={bloqueado} />
      {loading && <p>Cargando fondos y disponibilidad…</p>}
      {!loading && campanas.length === 0 && <p>No hay campañas cuyo destino final sea esta entidad.</p>}
      <Select label="Campaña" value={campanaId} onChange={setCampanaId} disabled={bloqueado}
        options={campanas.map(c => ({ value: c.id, label: c.nombre }))} placeholder="Selecciona el fondo" required />
      <Select label="Operación" value={tipo} onChange={v => setTipo(v === 'ingreso' ? 'ingreso' : 'egreso')}
        disabled={bloqueado} options={[{ value: 'egreso', label: 'Gasto del fondo' }, { value: 'ingreso', label: 'Ajuste de ingreso' }]} />
      {disponible !== undefined && <p>Disponible actual: <strong>{formatCurrency(disponible)}</strong></p>}
      {tipo === 'ingreso' && <p className="text-sm text-amber-800">Usa un ajuste solo para dinero que no tenga un cobro o envío ya registrado. Indica su origen en el motivo.</p>}
      <TextInput label="Importe" type="number" min={0.01} step={0.01} value={monto} onChange={setMonto} disabled={bloqueado} required />
      <DatePicker label="Fecha" value={fecha} onChange={setFecha} disabled={bloqueado} required />
      <MedioPagoFolio comprobante={comprobante} deshabilitado={bloqueado} />
      <Textarea label="Motivo y detalle" value={motivo} onChange={e => setMotivo(e.target.value)} disabled={bloqueado} required />
      {incierto && <p className="text-sm text-amber-800">Verifica este mismo movimiento antes de registrar otro.</p>}
      <div className="flex justify-end gap-3">
        <button type="button" disabled={submitting || incierto} onClick={requestClose} className="rounded-lg border px-4 py-2">Cancelar</button>
        <button type="submit" disabled={!autorizado || loading || submitting || comprobante.subiendo || campanas.length === 0}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-white disabled:opacity-50">{incierto ? 'Verificar movimiento' : 'Registrar'}</button>
      </div>
    </form>
  </DialogShell>;
}
export function MovimientoCampanaModal({ isOpen, ownerContext, ...props }: Props) {
  const { selectedLumisial } = useLumisial();
  const owner = ownerContext ?? (selectedLumisial ? { tipo: 'lumisial' as const, id: selectedLumisial.id, nombre: selectedLumisial.nombre } : null);
  if (!isOpen || !owner) return null;
  return <Formulario key={`${owner.tipo}:${owner.id}`} owner={owner} {...props} />;
}
